import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { Chart, LinearScale, LineElement, PointElement, ScatterController, Tooltip } from "chart.js";
import type { ChartDataset, ChartOptions, Plugin } from "chart.js";

import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { Box, IconButton, Tooltip as MuiTooltip, useTheme } from "@mui/material";

import { SelectionZoomPlugin } from "../../../../../../components/common/chart-plugins/selection-zoom";
import type {
  IChartZoomPayload,
  ISelectionZoomPluginOptions,
} from "../../../../../../components/common/chart-plugins/selection-zoom";
import { CHART_SELECTION_EVENTS, useChartZoom } from "../../../scheme/components/analytics/components/chart-zoom";
import { ChartZoomOverview } from "../../../scheme/components/analytics/components/chart-zoom-overview";
import { CASCADE_SORT_MODE, type TCascadeSortMode } from "../../../multi-analysis/types";
import {
  formatCascadeDate,
  getCascadeCursorOrder,
  parseCascadeWallClock,
  type ICascadeSample,
  type ICascadeTrendSeries,
} from "./model";
import { CASCADE_TOOLTIP_COLORS, getCascadeTooltipLines, getCascadeTooltipPosition } from "./tooltip-utils";

Chart.register(ScatterController, LinearScale, LineElement, PointElement, Tooltip, SelectionZoomPlugin);

const isSelectionActive = (chart: Chart) => {
  const state = (
    chart as Chart & {
      selectionZoom?: { mouseDown?: boolean; dragging?: boolean; suppressClickLock?: number };
    }
  ).selectionZoom;
  return Boolean(state?.mouseDown || state?.dragging || state?.suppressClickLock);
};

interface ITrendChartPoint {
  x: number;
  y: number;
  key: string;
  seriesKey: string;
  sample: ICascadeSample;
}

export interface ICascadeTrendChartProps {
  series: readonly ICascadeTrendSeries[];
  cursorKey: string | null;
  onCursorChange: (key: string) => void;
  formatValue: (value: number, unitCode: number | null) => string;
  sortMode: TCascadeSortMode;
  markerMode: boolean;
  hiddenSeriesKeys?: readonly string[];
  axisUnitCode?: number | null;
  axisUnits?: string;
}

type TrendChartInstance = Chart<"scatter", ITrendChartPoint[]>;
type TrendDataset = ChartDataset<"scatter", ITrendChartPoint[]>;

const extent = (values: readonly number[]): { min: number; max: number } => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (!Number.isFinite(value)) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return Number.isFinite(min) ? { min, max } : { min: 0, max: 1 };
};

const sampleX = (sample: ICascadeSample, sortMode: TCascadeSortMode): number => {
  if (sortMode === CASCADE_SORT_MODE.RPM) {
    return sample.rotationFrequencyRpm === null ? Number.NaN : sample.rotationFrequencyRpm;
  }
  return parseCascadeWallClock(sample.date) ?? Number.NaN;
};

export const CascadeTrendChart = ({
  series,
  cursorKey,
  onCursorChange,
  formatValue,
  sortMode,
  markerMode,
  hiddenSeriesKeys,
  axisUnitCode,
  axisUnits,
}: ICascadeTrendChartProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const hintId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipHoveredRef = useRef(false);
  const tooltipFocusedRef = useRef(false);
  const chartRef = useRef<TrendChartInstance | null>(null);
  const cursorRef = useRef(cursorKey);
  const onCursorChangeRef = useRef(onCursorChange);
  const hiddenSeriesRef = useRef(hiddenSeriesKeys);
  const suppressNextClickRef = useRef(false);
  cursorRef.current = cursorKey;
  onCursorChangeRef.current = onCursorChange;
  hiddenSeriesRef.current = hiddenSeriesKeys;
  const { zoomState, applySelectionZoom, clearZoomState, resetZoom, isZoomed, overview, panZoom, overviewPlugin } =
    useChartZoom(chartRef, {}, series);
  const applyZoom = useCallback(
    (payload: IChartZoomPayload) => {
      suppressNextClickRef.current = true;
      applySelectionZoom(payload);
    },
    [applySelectionZoom],
  );
  const chartLabel = formatMessage({ id: "pages.speed.cascade.trend" });
  const keyboardHint = formatMessage({ id: "pages.speed.cascade.chartKeyboardHint" });
  const xTitle =
    sortMode === CASCADE_SORT_MODE.RPM
      ? formatMessage({ id: "pages.scheme.analytics.rotationFrequencyRpmUnit" })
      : formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" });
  const visibleSeries = useMemo(
    () => series.filter(item => !hiddenSeriesKeys?.includes(item.key)),
    [hiddenSeriesKeys, series],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const textColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const font = { size: 12, family: theme.typography.fontFamily };
    const datasets: TrendDataset[] = series.map(item => ({
      label: item.name,
      data: item.samples.map(sample => ({
        x: sampleX(sample, sortMode),
        y: sample.value,
        key: sample.key,
        seriesKey: item.key,
        sample,
      })),
      borderColor: item.color,
      backgroundColor: item.color,
      borderWidth: 1.5,
      pointRadius: 2.5,
      pointHoverRadius: 5,
      pointHitRadius: 9,
      showLine: true,
      spanGaps: false,
      tension: 0,
      hidden: hiddenSeriesRef.current?.includes(item.key) ?? false,
    }));
    const pointIndexes = datasets.map(dataset => new Map(dataset.data.map((point, index) => [point.key, index])));
    const sourceByKey = new Map(series.map(item => [item.key, item]));
    const tooltipLabels = {
      point: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" }),
      measure: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" }),
      dateTime: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" }),
      value: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.value" }),
    };
    const tooltipLines = (point: ITrendChartPoint) => {
      const source = sourceByKey.get(point.seriesKey);
      if (!source) return [];
      return getCascadeTooltipLines(
        {
          pointName: source.pointName,
          measureName: source.measureName,
          date: point.sample.date,
          value: point.sample.value,
          unitCode: source.unitCode,
          units: source.units,
        },
        tooltipLabels,
        formatValue,
      );
    };
    const decoration: Plugin<"scatter"> = {
      id: "cascade-trend-decoration",
      afterDatasetsDraw: chart => {
        const context = chart.ctx;
        context.save();
        const selected = markerMode
          ? datasets.flatMap((dataset, datasetIndex) => {
              if (!chart.isDatasetVisible(datasetIndex)) return [];
              const index = cursorRef.current === null ? undefined : pointIndexes[datasetIndex].get(cursorRef.current);
              const point = index === undefined ? undefined : chart.getDatasetMeta(datasetIndex).data[index];
              return point && Number.isFinite(dataset.data[index!]?.x) && Number.isFinite(dataset.data[index!]?.y)
                ? [point]
                : [];
            })[0]
          : undefined;
        if (selected) {
          context.beginPath();
          context.setLineDash([3, 4]);
          context.strokeStyle = textColor;
          context.lineWidth = 1;
          context.moveTo(selected.x, chart.chartArea.top);
          context.lineTo(selected.x, chart.chartArea.bottom);
          context.stroke();
          context.setLineDash([]);
        }
        datasets.forEach((dataset, datasetIndex) => {
          if (!chart.isDatasetVisible(datasetIndex)) return;
          const index = cursorRef.current === null ? undefined : pointIndexes[datasetIndex].get(cursorRef.current);
          const cursorPoint = index === undefined ? undefined : chart.getDatasetMeta(datasetIndex).data[index];
          if (
            cursorPoint &&
            Number.isFinite(dataset.data[index!]?.x) &&
            Number.isFinite(dataset.data[index!]?.y) &&
            cursorPoint.x >= chart.chartArea.left &&
            cursorPoint.x <= chart.chartArea.right &&
            cursorPoint.y >= chart.chartArea.top &&
            cursorPoint.y <= chart.chartArea.bottom
          ) {
            context.beginPath();
            context.arc(cursorPoint.x, cursorPoint.y, 6, 0, Math.PI * 2);
            context.fillStyle = theme.palette.background.paper;
            context.fill();
            context.strokeStyle = theme.palette.common.black;
            context.lineWidth = 2;
            context.stroke();
          }
        });
        context.restore();
      },
    };
    const disabledPlugins = {
      crosshair: false,
      alarmLevel: false,
      htmlLegend: false,
      drawConnectingLines: false,
      drawOrbitConnectingLines: false,
      harmonicCursors: false,
      subHarmonicCursors: false,
      additionalCursorLabels: false,
      faultFrequenciesCursors: false,
      markerPassageLines: false,
    } as const;
    const selectionZoom: ISelectionZoomPluginOptions = {
      enabled: true,
      captureOutsideCanvas: true,
      minDragPx: 8,
      xScaleId: "x",
      yScaleIds: ["y"],
      onZoom: applyZoom,
      onReset: clearZoomState,
    };
    const xExtent = extent(datasets.flatMap(dataset => dataset.data.map(point => point.x)));
    const xMin = xExtent.min;
    const xMax = xExtent.max;
    const xPadding = xMin === xMax ? Math.max(1, Math.abs(xMin) * 0.01) : (xMax - xMin) * 0.02;
    const yZoom = zoomState?.y?.y;
    const options: ChartOptions<"scatter"> = {
      animation: false,
      responsive: false,
      maintainAspectRatio: false,
      events: CHART_SELECTION_EVENTS,
      interaction: { mode: "nearest", intersect: false },
      onClick: (event, elements, clickedChart) => {
        if (event.type !== "click") return;
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false;
          return;
        }
        if (isSelectionActive(clickedChart)) return;
        const selected = elements[0];
        const point = selected ? datasets[selected.datasetIndex]?.data[selected.index] : null;
        if (point) onCursorChangeRef.current(point.key);
      },
      scales: {
        x: {
          type: "linear",
          min: zoomState?.x?.min ?? xMin - xPadding,
          max: zoomState?.x?.max ?? xMax + xPadding,
          title: { display: true, text: xTitle, color: textColor, font },
          grid: { color: gridColor },
          ticks: {
            color: textColor,
            font,
            maxTicksLimit: 8,
            maxRotation: 0,
            callback: value =>
              sortMode === CASCADE_SORT_MODE.TIME
                ? formatCascadeDate(new Date(Number(value)).toISOString())
                : formatValue(Number(value), null),
          },
        },
        y: {
          type: "linear",
          position: "left",
          beginAtZero: !yZoom,
          ...(yZoom ? { min: yZoom.min, max: yZoom.max, grace: 0 } : { grace: "10%" as const }),
          title: {
            display: true,
            text: `${chartLabel}, ${axisUnits ?? series[0]?.units ?? ""}`,
            color: textColor,
            font,
          },
          ticks: {
            color: textColor,
            font,
            maxTicksLimit: 6,
            callback: value => formatValue(Number(value), axisUnitCode ?? series[0]?.unitCode ?? null),
          },
          grid: { color: gridColor },
          afterFit: axis => {
            axis.width = 82;
          },
        },
      },
      plugins: {
        ...disabledPlugins,
        legend: { display: false },
        ...{ selectionZoom },
        tooltip: {
          enabled: false,
          external: ({ chart: tooltipChart, tooltip }) => {
            const element = tooltipRef.current;
            if (!element) return;
            if (
              isSelectionActive(tooltipChart) ||
              (!tooltip.opacity && !tooltipHoveredRef.current && !tooltipFocusedRef.current)
            ) {
              element.style.display = "none";
              return;
            }
            if (!tooltip.opacity) return;
            const points = [
              ...new Map(
                tooltip.dataPoints.map(item => {
                  const point = datasets[item.datasetIndex]?.data[item.dataIndex];
                  return [point?.key, point] as const;
                }),
              ).values(),
            ].filter((point): point is ITrendChartPoint => Boolean(point));
            const anchor = tooltip.dataPoints[0]?.element;
            if (!anchor || !points.length) {
              element.style.display = "none";
              return;
            }
            element.replaceChildren(
              ...points.flatMap((point, index) =>
                tooltipLines(point).map((line, row) => {
                  const text = document.createElement("div");
                  text.textContent = line;
                  text.style.fontWeight = row === 0 ? "600" : "400";
                  text.style.color = row === 0 ? CASCADE_TOOLTIP_COLORS.title : CASCADE_TOOLTIP_COLORS.body;
                  if (index > 0 && row === 0) text.style.marginTop = "8px";
                  return text;
                }),
              ),
            );
            element.style.display = "block";
            const position = getCascadeTooltipPosition(
              anchor,
              { width: element.offsetWidth, height: element.offsetHeight },
              { width: container.clientWidth, height: container.clientHeight },
            );
            element.style.left = `${position.x}px`;
            element.style.top = `${position.y}px`;
          },
        },
      },
    };
    const chart = new Chart<"scatter", ITrendChartPoint[]>(canvas, {
      type: "scatter",
      data: { datasets },
      options,
      plugins: [decoration, overviewPlugin],
    });
    chartRef.current = chart;
    let resizeFrame = 0;
    const resize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        const width = container.clientWidth;
        const height = container.clientHeight;
        if (width <= 0 || height <= 0 || (chart.width === width && chart.height === height)) return;
        chart.resize(width, height);
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => {
      cancelAnimationFrame(resizeFrame);
      observer.disconnect();
      chartRef.current = null;
      tooltipHoveredRef.current = false;
      tooltipFocusedRef.current = false;
      if (tooltipRef.current) tooltipRef.current.style.display = "none";
      chart.destroy();
    };
  }, [
    applyZoom,
    axisUnitCode,
    axisUnits,
    chartLabel,
    clearZoomState,
    formatMessage,
    formatValue,
    markerMode,
    overviewPlugin,
    series,
    sortMode,
    theme.palette,
    theme.typography.fontFamily,
    xTitle,
    zoomState,
  ]);

  useEffect(() => {
    resetZoom();
  }, [resetZoom, series, sortMode]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const hidden = new Set(hiddenSeriesKeys ?? []);
    chart.data.datasets.forEach((dataset, index) =>
      chart.setDatasetVisibility(index, !hidden.has(dataset.data[0]?.seriesKey ?? "")),
    );
    const visible = series.find(item => !hidden.has(item.key));
    const scale = chart.options.scales?.y;
    if (visible && scale) {
      scale.title = {
        display: true,
        text: `${chartLabel}, ${visible.units}`,
        color: scale.title?.color,
        font: scale.title?.font,
      };
      scale.ticks = {
        ...scale.ticks,
        callback: value => formatValue(Number(value), visible.unitCode),
      };
    }
    chart.update("none");
  }, [chartLabel, formatValue, hiddenSeriesKeys, series]);

  useEffect(() => {
    chartRef.current?.draw();
  }, [cursorKey]);

  const cursorOrder = useMemo(
    () => getCascadeCursorOrder(visibleSeries, cursorKey, sortMode),
    [cursorKey, sortMode, visibleSeries],
  );

  return (
    <Box
      ref={containerRef}
      data-cascade-chart="trend"
      data-cascade-cursor-key={cursorKey ?? ""}
      data-cascade-zoomed={isZoomed ? "true" : "false"}
      onMouseOutCapture={event => {
        const next = event.relatedTarget;
        tooltipHoveredRef.current = next instanceof Node && Boolean(tooltipRef.current?.contains(next));
      }}
      sx={{ flex: 1, minWidth: 0, minHeight: 0, position: "relative", overflow: "hidden" }}
    >
      <ChartZoomOverview overview={overview} zoomState={zoomState} onPan={panZoom} />
      {isZoomed ? (
        <MuiTooltip title={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}>
          <IconButton
            size="small"
            aria-label={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}
            onClick={resetZoom}
            sx={{ position: "absolute", top: 4, left: 4, zIndex: 2, bgcolor: "background.paper" }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      ) : null}
      <canvas
        ref={canvasRef}
        role="img"
        tabIndex={0}
        aria-label={chartLabel}
        aria-describedby={hintId}
        onDoubleClick={resetZoom}
        onMouseDownCapture={event => {
          if (event.button === 0) {
            suppressNextClickRef.current = false;
            tooltipHoveredRef.current = false;
            tooltipFocusedRef.current = false;
            if (tooltipRef.current) tooltipRef.current.style.display = "none";
          }
          if (event.button === 1 && isZoomed) {
            event.preventDefault();
            resetZoom();
          }
        }}
        onKeyDown={event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === "Escape") {
            resetZoom();
            return;
          }
          if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
          const current = cursorOrder.findIndex(sample => sample.key === cursorKey);
          const nextIndex =
            event.key === "ArrowLeft"
              ? Math.max(0, current < 0 ? cursorOrder.length - 1 : current - 1)
              : Math.min(cursorOrder.length - 1, current + 1);
          const next = cursorOrder[nextIndex];
          if (next) {
            event.preventDefault();
            onCursorChange(next.key);
          }
        }}
        style={{
          position: "absolute",
          inset: 0,
          display: "block",
          width: "100%",
          height: "100%",
          cursor: markerMode ? "crosshair" : undefined,
        }}
      >
        {chartLabel}
      </canvas>
      <Box
        ref={tooltipRef}
        role="tooltip"
        tabIndex={0}
        data-cascade-tooltip="trend"
        onMouseEnter={() => {
          tooltipHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          tooltipHoveredRef.current = false;
          if (!tooltipFocusedRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onFocus={() => {
          tooltipFocusedRef.current = true;
        }}
        onBlur={() => {
          tooltipFocusedRef.current = false;
          if (!tooltipHoveredRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onKeyDown={event => {
          if (event.key === "Escape") {
            event.stopPropagation();
            tooltipFocusedRef.current = false;
            tooltipHoveredRef.current = false;
            if (tooltipRef.current) tooltipRef.current.style.display = "none";
            canvasRef.current?.focus();
          }
        }}
        sx={{
          display: "none",
          position: "absolute",
          pointerEvents: "auto",
          zIndex: 3,
          maxWidth: "min(360px, calc(100% - 8px))",
          maxHeight: "calc(100% - 8px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
          boxSizing: "border-box",
          p: 1,
          borderRadius: 1,
          bgcolor: CASCADE_TOOLTIP_COLORS.background,
          color: CASCADE_TOOLTIP_COLORS.body,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily,
          lineHeight: 1.5,
          overflowWrap: "anywhere",
          boxShadow: 3,
        }}
      />
      <Box
        id={hintId}
        component="span"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clipPath: "inset(50%)",
        }}
      >
        {keyboardHint}
      </Box>
    </Box>
  );
};
